<template>
  <div class="space-y-8">
    <section v-if="!hasBusiness && !serviceListLoading" class="rounded-[32px] border border-orange-100 bg-[linear-gradient(180deg,#fffdfa_0%,#fff7f0_100%)] px-6 py-12 shadow-sm lg:px-10 lg:py-16">
      <div class="mx-auto flex max-w-3xl flex-col items-center text-center">
        <div class="flex h-20 w-20 items-center justify-center rounded-full bg-[linear-gradient(135deg,#fff1e3_0%,#ffffff_100%)] text-4xl shadow-sm ring-1 ring-orange-100">
          <i class="ri-building-4-line text-brand"></i>
        </div>
        <h2 class="mt-6 text-4xl font-semibold tracking-tight text-ink">Welcome to VendorHub</h2>
        <p class="mt-4 max-w-2xl text-lg leading-8 text-muted">
          Register your business entity, then add venue properties or service vendor brands under it.
        </p>

        <div class="mt-10 grid w-full max-w-2xl gap-4 rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm md:grid-cols-3">
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-building-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Business</p>
            <p class="mt-1 text-sm text-muted">Legal entity, GST, bank</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-bank-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Venue</p>
            <p class="mt-1 text-sm text-muted">Property + spaces + menus</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-camera-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Service vendor</p>
            <p class="mt-1 text-sm text-muted">Photography, catering, decor...</p>
          </div>
        </div>

        <button
          type="button"
          class="mt-10 rounded-2xl bg-brand px-8 py-4 text-base font-semibold text-white shadow-sm transition hover:bg-brandDark"
          @click="navigateToBusinessEditor()"
        >
          Register your first business
        </button>
      </div>
    </section>

    <section v-else class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div class="mb-6 grid gap-3 sm:grid-cols-3">
        <div v-for="item in dashboardStats" :key="item.label" class="rounded-2xl border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">{{ item.label }}</p>
              <p class="mt-2 text-2xl font-semibold text-ink">{{ item.value }}</p>
            </div>
            <span class="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-xl text-brand shadow-sm">
              <i :class="item.icon"></i>
            </span>
          </div>
        </div>
      </div>

      <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-lg font-semibold text-ink">Businesses</h3>
          <p class="text-sm text-muted">Manage your business records here.</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" :disabled="serviceListLoading" @click="fetchBusinessVendors()">
            {{ serviceListLoading ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="navigateToBusinessEditor()">
            {{ hasBusiness ? 'Add another business' : 'Add business' }}
          </button>
        </div>
      </div>

      <div v-if="serviceListError" class="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
        {{ serviceListError }}
      </div>

      <div v-if="serviceListLoading" class="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-muted">
        Loading business vendors...
      </div>

      <TransitionGroup
        v-else-if="serviceVendors.length"
        tag="div"
        name="business-card"
        class="grid gap-4 md:grid-cols-2"
      >
        <article
          v-for="(business, index) in serviceVendors"
          :key="business.id"
          class="business-card-item group relative overflow-visible rounded-[20px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffdfb_0%,#fff8f2_100%)] p-4 shadow-[0_10px_26px_rgba(91,70,56,0.06)] transition duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_18px_34px_rgba(91,70,56,0.12)]"
          :style="{ animationDelay: `${Math.min(index, 8) * 70}ms` }"
        >
          <div class="business-card-main">
            <div
              class="flex w-full cursor-pointer gap-4 text-left"
              role="button"
              tabindex="0"
              @click="navigateToBusinessDetail(business)"
              @keydown.enter.prevent="navigateToBusinessDetail(business)"
              @keydown.space.prevent="navigateToBusinessDetail(business)"
            >
              <div class="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-orange-100 bg-white sm:h-24 sm:w-24">
                <img
                  :src="getBusinessImageSrc(business.image)"
                  :alt="business.businessName"
                  class="h-full w-full object-contain p-2 transition duration-300 group-hover:scale-105"
                >
              </div>

              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="truncate text-[1.1rem] font-semibold leading-tight text-ink sm:text-[1.2rem]">{{ business.businessName }}</p>
                    <p class="mt-1 text-xs font-semibold uppercase tracking-wide text-brand">{{ business.badge }}</p>
                  </div>
                  <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-white ring-1 ring-brand transition group-hover:bg-brand group-hover:text-white">
                    <i class="ri-arrow-right-up-line !text-white"></i>
                  </span>
                </div>
                <div v-if="business.address" class="mt-3 text-sm leading-5 text-muted">
                  <p>
                    <i class="ri-map-pin-2-line mr-1 text-brand"></i>
                    <span>{{ getBusinessAddressText(business) }}</span>
                    <button
                      v-if="shouldShowAddressToggle(business.address)"
                      type="button"
                      class="ml-2 inline-flex text-xs font-semibold text-brand transition hover:text-brandDark"
                      @click.stop="toggleAddressExpansion(business.id)"
                    >
                      {{ isBusinessAddressExpanded(business.id) ? 'Less' : 'More' }}
                    </button>
                  </p>
                </div>
                <p v-else class="mt-3 text-sm text-muted">Address not added</p>
              </div>
            </div>

            <div class="mt-4 flex items-center justify-between gap-3 border-t border-[#eadfd4]/80 pt-3">
              <div class="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold text-muted">
                <i class="ri-user-3-line shrink-0 text-brand"></i>
                <span class="min-w-0 truncate">{{ business.ownerName }}</span>
              </div>
              <div class="flex shrink-0 flex-wrap justify-end gap-2">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-full border border-[#eadfd4] bg-white px-3 py-1.5 text-xs font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand"
                  :aria-label="`Edit ${business.businessName}`"
                  @click="navigateToBusinessEditor(business, 'business')"
                >
                  <i class="ri-edit-line"></i>
                  Edit
                </button>
                <button
                  type="button"
                  class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-100 bg-white text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                  :aria-label="`Delete ${business.businessName}`"
                  :disabled="deletingBusinessId === String(business.id)"
                  @click="deleteBusiness(business)"
                >
                  <i :class="deletingBusinessId === String(business.id) ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'"></i>
                </button>
              </div>
            </div>
          </div>
        </article>
      </TransitionGroup>

    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'
import Swal from 'sweetalert2'

const router = useRouter()

const serviceVendors = ref([])
const vendorId = ref(getAuthUserId())
const serviceListLoading = ref(false)
const serviceListError = ref('')
const expandedAddressIds = ref(new Set())
const venuesCount = ref(0)
const enquiriesCount = ref(0)
const deletingBusinessId = ref('')
const BUSINESS_IMAGE_PLACEHOLDER = 'https://dummyimage.com/960x640/fff1e6/8b5e3c&text=Business'

const hasBusiness = computed(() => serviceVendors.value.length > 0)
const dashboardStats = computed(() => [
  { label: 'Businesses', value: serviceVendors.value.length, icon: 'ri-building-4-line' },
  { label: 'Enquiries', value: enquiriesCount.value, icon: 'ri-question-answer-line' },
  { label: 'Venues', value: venuesCount.value, icon: 'ri-map-pin-2-line' }
])

const hasBusinessImage = (value) => {
  if (value == null) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0'
}

const ADDRESS_PREVIEW_LIMIT = 50
const getBusinessImageSrc = (value) => (hasBusinessImage(value) ? value : BUSINESS_IMAGE_PLACEHOLDER)
const shouldShowAddressToggle = (value) => String(value || '').trim().length > ADDRESS_PREVIEW_LIMIT
const isBusinessAddressExpanded = (businessId) => expandedAddressIds.value.has(String(businessId))
const getBusinessAddressText = (business) => {
  const address = String(business?.address || '').trim()
  if (!address || isBusinessAddressExpanded(business.id) || address.length <= ADDRESS_PREVIEW_LIMIT) {
    return address
  }
  return `${address.slice(0, ADDRESS_PREVIEW_LIMIT).trim()}...`
}
const toggleAddressExpansion = (businessId) => {
  const id = String(businessId)
  const next = new Set(expandedAddressIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  expandedAddressIds.value = next
}

const normalizeImage = (item) => item?.img || item?.image || item?.business_img || item?.photo || ''

const normalizeBusinessVendor = (item, index) => ({
  id: item?.id || item?.business_vendor_id || item?.vendor_id || item?.business_id || `vendor-${index}`,
  sId: item?.s_id || item?.seller_id || item?.vendor_owner_id || '',
  businessName: item?.business_name || item?.name || item?.title || 'Untitled business vendor',
  ownerName: item?.owner_name || item?.owner || item?.contact_name || 'Owner not available',
  description: item?.description || item?.about || item?.details || 'No description available.',
  image: normalizeImage(item),
  phoneCode: item?.phone_code ?? '',
  phone: item?.phone ?? '',
  email: item?.email ?? '',
  address: item?.address ?? item?.location ?? '',
  website: item?.website ?? '',
  latitude: item?.latitude ?? '',
  longitude: item?.longitude ?? '',
  doc1: item?.doc1 ?? '',
  doc2: item?.doc2 ?? '',
  doc3: item?.doc3 ?? '',
  badge: item?.status || item?.type || 'Business vendor'
})

const getListCollections = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) {
    return {
      vendorItems: payload
    }
  }
  return {
    vendorItems:
      payload?.vendors ||
      payload?.business_vendors ||
      payload?.businessVendor ||
      payload?.items ||
      payload?.list ||
      []
  }
}

const getVenueItems = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  return payload?.venues || payload?.items || payload?.list || payload?.venue_list || []
}

const getEnquiryItems = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  return payload?.enquiries || payload?.venue_enquiries || payload?.service_vendor_enquiries || payload?.items || payload?.list || []
}

const fetchVenueCount = async (businesses = []) => {
  const responses = await Promise.all(
    businesses.map(async (business) => {
      const businessId = String(business?.id || business?.business_id || business?.business_vendor_id || '').trim()
      if (!businessId) return 0
      const formData = new FormData()
      formData.append('business_id', businessId)
      const { data } = await api.listVenuesBusinessVendor(formData)
      return getVenueItems(data).length
    })
  )
  venuesCount.value = responses.reduce((total, count) => total + count, 0)
}

const fetchEnquiriesCount = async () => {
  const formData = new FormData()
  formData.append('vendor_id', vendorId.value)
  formData.append('s_id', vendorId.value)
  formData.append('type', 'venue')
  formData.append('service_vendor_id', '')
  const { data } = await api.getEnquiries(formData)
  enquiriesCount.value = getEnquiryItems(data).length
}

const fetchBusinessVendors = async () => {
  vendorId.value = getAuthUserId()
  serviceListError.value = ''

  if (!vendorId.value) {
    serviceListError.value = 'Vendor id (`s_id`) is not available in auth storage.'
    serviceVendors.value = []
    return
  }

  serviceListLoading.value = true
  try {
    const formData = new FormData()
    formData.append('s_id', vendorId.value)
    const { data } = await api.listBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to load business vendors')
    }

    const collections = getListCollections(data)
    const vendorItems = Array.isArray(collections.vendorItems) ? collections.vendorItems : []
    const normalizedVendors = vendorItems.map(normalizeBusinessVendor)

    serviceVendors.value = normalizedVendors
    await Promise.allSettled([
      fetchVenueCount(vendorItems),
      fetchEnquiriesCount()
    ])
  } catch (error) {
    serviceListError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to load business vendors'
    serviceVendors.value = []
  } finally {
    serviceListLoading.value = false
  }
}

const deleteBusiness = async (business) => {
  const id = String(business?.id || '').trim()
  if (!id) return
  const result = await Swal.fire({
    title: 'Delete business?',
    text: `${business.businessName} will be removed from your dashboard.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel'
  })
  if (!result.isConfirmed) return

  deletingBusinessId.value = id
  serviceListError.value = ''
  try {
    const formData = new FormData()
    formData.append('business_id', id)
    formData.append('id', id)
    const { data } = await api.deleteBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to delete business')
    }
    serviceVendors.value = serviceVendors.value.filter((item) => String(item.id) !== id)
    await fetchBusinessVendors()
  } catch (error) {
    serviceListError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to delete business'
  } finally {
    deletingBusinessId.value = ''
  }
}

const navigateToBusinessEditor = (business = null, tab = 'business') => {
  if (business?.id) {
    router.push({
      name: 'business-edit',
      params: { id: business.id },
      query: { tab }
    })
    return
  }

  router.push({
    name: 'business-create',
    query: { tab: 'business' }
  })
}

const navigateToBusinessDetail = (business = null) => {
  if (!business?.id) return
  router.push({
    name: 'business-detail',
    params: { id: business.id }
  })
}

onMounted(() => {
  fetchBusinessVendors()
})
</script>

<style scoped>
.business-card-item {
  animation: business-card-enter 0.42s ease both;
}

.business-card-enter-active,
.business-card-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.business-card-enter-from,
.business-card-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.business-card-move {
  transition: transform 0.24s ease;
}

@keyframes business-card-enter {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
